import React from 'react';
import { useTranslation } from 'react-i18next';
import { PLAN_LABELS, PLAN_AMOUNTS } from './useBusinessDashboard';
import usePricingConfig from '../../hooks/usePricingConfig';
import {
  Clock, CheckCircle, AlertCircle, CreditCard, Calendar, Mail, Loader2, RefreshCw, ArrowUpCircle
} from 'lucide-react';

const ICON_MAP = { Clock, CheckCircle, AlertCircle, Calendar };

export const SubscriptionSection = ({ business, statusInfo, retrying, handleRetryPayment, handleOpenPortal, handleUpgrade, portalLoading }) => {
  const { t } = useTranslation();
  const pricing = usePricingConfig();
  if (!business) return null;

  const StatusIcon = ICON_MAP[statusInfo?.icon] || AlertCircle;

  // Free Directory (€0) listings never owe a payment. Without this flag the
  // dashboard mis-reads the correct `paymentConfirmed:false` free state as an
  // unpaid account and shows a false "Pending" / "Complete Payment" prompt.
  const isFree = business.subscriptionPlan === 'free';
  const canUpgradeFromFree = isFree
    && business.subscriptionStatus === 'active'
    && business.approvalStatus === 'approved';


  return (
    <>
      {/* Email unverified banner */}
      {!business.emailVerified && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="email-unverified-banner">
          <Mail className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-amber-900">{t('emailNotVerified')}</p>
            <p className="text-sm text-amber-700 mt-1">{t('emailNotVerifiedDesc')}</p>
          </div>
        </div>
      )}

      {/* Status Card */}
      {statusInfo && (
        <div className={`bg-white rounded-xl shadow-sm p-6 border-l-4 ${
          statusInfo.color === 'green' ? 'border-green-500' :
          statusInfo.color === 'yellow' ? 'border-yellow-500' :
          statusInfo.color === 'orange' ? 'border-orange-500' : 'border-red-500'
        }`} data-testid="status-card">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-full ${
              statusInfo.color === 'green' ? 'bg-green-100' :
              statusInfo.color === 'yellow' ? 'bg-yellow-100' :
              statusInfo.color === 'orange' ? 'bg-orange-100' : 'bg-red-100'
            }`}>
              <StatusIcon className={`h-6 w-6 ${
                statusInfo.color === 'green' ? 'text-green-600' :
                statusInfo.color === 'yellow' ? 'text-yellow-600' :
                statusInfo.color === 'orange' ? 'text-orange-600' : 'text-red-600'
              }`} />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900" data-testid="status-title">{statusInfo.title}</h3>
              <p className="text-sm text-gray-600">{statusInfo.subtitle}</p>
            </div>
            {/* Inline Subscribe / Activate CTA — guarantees a visible action
                whenever the listing is NOT active, regardless of which secondary
                banner conditions match below.
                NB: skipped for `trial` and `paused` statuses — the dashboard's
                TrialBanner owns those CTAs to avoid two contradictory prompts. */}
            {business.subscriptionStatus !== 'active'
              && business.subscriptionStatus !== 'trial'
              && business.subscriptionStatus !== 'paused'
              && business.approvalStatus === 'approved' && (
              <button onClick={handleRetryPayment} disabled={retrying}
                className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 flex-shrink-0 disabled:opacity-50 font-medium"
                data-testid="status-card-subscribe-btn">
                {retrying ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> {t('processing')}</>
                ) : (
                  <><CreditCard className="h-4 w-4" /> {t('subscribeNow', 'Subscribe Now')}</>
                )}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Payment-required Banner — shown for ANY non-paid, non-active state
          (pending / none / null / trial-leftover / undefined / etc.). The two
          "reactivate" states (cancelled, expired) get their own dedicated red
          banner below; everything else funnels through here so users always
          have a clear path to activate.
          NB: trial and paused accounts are excluded — the dashboard's
          TrialBanner owns those messages. */}
      {!business.paymentConfirmed
        && business.subscriptionStatus !== 'active'
        && business.subscriptionStatus !== 'cancelled'
        && business.subscriptionStatus !== 'expired'
        && business.subscriptionStatus !== 'trial'
        && business.subscriptionStatus !== 'paused' && (
        <div className="rounded-xl p-5 bg-amber-50 border border-amber-200" data-testid="payment-required-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-amber-600" />
            <div className="flex-1">
              <h4 className="font-semibold text-amber-900">
                {t('paymentRequiredTitle', 'Activate your listing')}
              </h4>
              <p className="text-sm mt-1 text-amber-700">
                {`Your business is currently on the ${pricing.foundingTitle}. ${pricing.foundingNoCardRequired ? 'No card required.' : ''}`}
              </p>
              <button onClick={handleRetryPayment} disabled={retrying}
                className="mt-3 px-4 py-2 text-white rounded-lg transition-colors flex items-center gap-2 text-sm bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                data-testid="renewal-subscribe-btn">
                {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                {retrying ? t('processing') : t('subscribeNow', 'Subscribe / Activate Listing')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Expired / Cancelled — Reactivation banner */}
      {(business.subscriptionStatus === 'cancelled' || business.subscriptionStatus === 'expired') && (
        <div className="rounded-xl p-5 bg-red-50 border border-red-200" data-testid="reactivate-subscription-banner">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 mt-0.5 flex-shrink-0 text-red-600" />
            <div className="flex-1">
              <h4 className="font-semibold text-red-900">
                {t('reactivateTitle', 'Your subscription has ended')}
              </h4>
              <p className="text-sm mt-1 text-red-700">
                {t('reactivateDesc', 'Your listing is no longer visible on Discover. Reactivate now to restore visibility — your profile, photos, reviews and events are all preserved.')}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={handleRetryPayment} disabled={retrying}
                  className="px-4 py-2 text-white rounded-lg transition-colors flex items-center gap-2 text-sm bg-red-600 hover:bg-red-700 disabled:opacity-50"
                  data-testid="reactivate-subscription-btn">
                  {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                  {retrying ? t('processing') : t('reactivateNow', 'Reactivate Now')}
                </button>
                {business.stripeCustomerId && (
                  <button onClick={handleOpenPortal} disabled={portalLoading}
                    className="px-4 py-2 bg-white text-red-700 border border-red-300 rounded-lg hover:bg-red-50 transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
                    data-testid="reactivate-portal-btn">
                    {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                    {t('manageBilling', 'Manage Billing')}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Details Card */}
      <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="subscription-card">
        <div className="p-4 border-b">
          <h2 className="font-semibold text-gray-900 flex items-center gap-2">
            <CreditCard className="h-5 w-5 text-purple-600" />
            {t('subscriptionDetails')}
          </h2>
        </div>
        <div className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{t('plan')}</p>
              <p className="font-semibold text-gray-900 mt-1" data-testid="plan-name">
                {PLAN_LABELS[business.subscriptionPlan] || t('noPlanSelected')}
              </p>
              <p className="text-sm text-purple-600">{PLAN_AMOUNTS[business.subscriptionPlan] || '-'}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{t('status')}</p>
              <p className="font-semibold text-gray-900 mt-1 capitalize" data-testid="subscription-status">
                {business.subscriptionStatus}
              </p>
              <p className="text-sm text-gray-500">
                {business.subscriptionStatus === 'active' && t('fullyActive')}
              </p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{t('approval')}</p>
              <p className={`font-semibold mt-1 capitalize ${
                business.approvalStatus === 'approved' ? 'text-green-700' :
                business.approvalStatus === 'pending' ? 'text-yellow-700' : 'text-red-700'
              }`} data-testid="approval-status">
                {business.approvalStatus}
              </p>
            </div>
          {/* Payment cell: for trial accounts, show "Free trial" instead of
              "Pending" (which suggests a problem when in fact it's expected). */}
          <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500 uppercase tracking-wide">{t('payment')}</p>
              <p className={`font-semibold mt-1 ${
                isFree ? 'text-green-700'
                : business.paymentConfirmed ? 'text-green-700'
                : business.subscriptionStatus === 'trial' ? 'text-emerald-700'
                : business.subscriptionStatus === 'paused' ? 'text-red-700'
                : 'text-amber-700'
              }`} data-testid="payment-status">
                {isFree ? t('paymentNotRequired', 'Not required')
                  : business.paymentConfirmed ? t('confirmed')
                  : business.subscriptionStatus === 'trial' ? t('trial.paymentLabel', 'Free trial')
                  : business.subscriptionStatus === 'paused' ? t('trial.pausedLabel', 'Paused')
                  : t('pending')}
              </p>
            </div>
            {business.subscriptionEnd && (
              <div className="bg-gray-50 p-3 rounded-lg col-span-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide">
                  {t('subscriptionEnds')}
                </p>
                <p className="font-semibold text-gray-900 mt-1" data-testid="end-date">
                  {new Date(business.subscriptionEnd).toLocaleDateString('en-US', {
                    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
                  })}
                </p>
              </div>
            )}
          </div>

          {/* Retry Payment / Manage Billing — suppressed for trial / paused
              (TrialBanner covers them). */}
          {!business.paymentConfirmed
            && !isFree
            && business.subscriptionStatus !== 'trial'
            && business.subscriptionStatus !== 'paused' ? (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="font-medium text-amber-900">{t('paymentNotCompleted')}</p>
                  <p className="text-sm text-amber-700 mt-1">{t('completePaymentDesc')}</p>
                </div>
                <button onClick={handleRetryPayment} disabled={retrying}
                  className="px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 flex-shrink-0 disabled:opacity-50"
                  data-testid="retry-payment-btn">
                  {retrying ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> {t('processing')}</>
                  ) : (
                    <><RefreshCw className="h-4 w-4" /> {t('completePayment')}</>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Free Directory → paid upgrade. Free listings are permanently
                  free; this simply offers the optional paid tiers. Each option
                  routes through the existing Stripe Checkout (60-day free trial,
                  card collected up front) via handleUpgrade(). */}
              {canUpgradeFromFree && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-5" data-testid="free-upgrade-banner">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                      <ArrowUpCircle className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-purple-900">
                        {t('upgradeFromFreeTitle', 'Upgrade for more visibility & features')}
                      </h4>
                      <p className="text-sm text-purple-700 mt-1">
                        {t('upgradeFromFreeDesc', 'Your Free Directory listing stays free forever. Upgrade any time to unlock events, offers and more — every paid plan starts with a 60-day free trial, cancel anytime.')}
                      </p>
                      <div className="mt-4 grid gap-2 sm:grid-cols-3">
                        <button onClick={() => handleUpgrade('basic_monthly')} disabled={portalLoading}
                          className="px-4 py-3 bg-white border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors text-left disabled:opacity-50"
                          data-testid="upgrade-basic-btn">
                          <span className="block font-semibold text-purple-900">Basic</span>
                          <span className="block text-sm text-purple-700">€1.99 / month</span>
                        </button>
                        <button onClick={() => handleUpgrade('plus_monthly')} disabled={portalLoading}
                          className="px-4 py-3 bg-white border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors text-left disabled:opacity-50"
                          data-testid="upgrade-plus-monthly-btn">
                          <span className="block font-semibold text-purple-900">Business Plus</span>
                          <span className="block text-sm text-purple-700">€2.99 / month</span>
                        </button>
                        <button onClick={() => handleUpgrade('plus_annual')} disabled={portalLoading}
                          className="px-4 py-3 bg-white border border-purple-300 rounded-lg hover:bg-purple-50 transition-colors text-left disabled:opacity-50"
                          data-testid="upgrade-plus-annual-btn">
                          <span className="block font-semibold text-purple-900">Business Plus · Annual</span>
                          <span className="block text-sm text-purple-700">€29.99 / year</span>
                        </button>
                      </div>
                      {portalLoading && (
                        <p className="mt-3 text-sm text-purple-700 flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin" /> {t('processing')}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Upgrade Prompt — only when subscription is currently active.
                  Prevents stale paymentConfirmed=true on a cancelled/expired/
                  none-state account from showing an "Upgrade Now" CTA that
                  would skip the actual activation flow. */}
              {business.subscriptionStatus === 'active'
                && (business.subscriptionPlan === 'basic' || business.subscriptionPlan === '6months') && (
                <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-5" data-testid="upgrade-plan-banner">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-purple-100 rounded-lg flex-shrink-0">
                      <ArrowUpCircle className="h-5 w-5 text-purple-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-semibold text-purple-900">
                        {business.subscriptionPlan === 'basic'
                          ? t('upgradeFromBasicTitle', 'Unlock events, offers & features')
                          : t('upgradeTitle', 'Save by Upgrading to 12 Months')}
                      </h4>
                      <p className="text-sm text-purple-700 mt-1">
                        {business.subscriptionPlan === 'basic'
                          ? `All businesses are currently on the ${pricing.foundingTitle}. ${pricing.foundingIncludesLine}`
                          : t('upgradeDesc')}
                      </p>
                      <button onClick={() => handleUpgrade('12months')} disabled={portalLoading}
                        className="mt-3 px-5 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-2 text-sm font-medium disabled:opacity-50"
                        data-testid="upgrade-plan-btn">
                        {portalLoading ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> {t('processing')}</>
                        ) : (
                          <><ArrowUpCircle className="h-4 w-4" /> {t('upgradeNow', 'Upgrade Now')}</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Manage Billing */}
              {business.stripeCustomerId && (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="font-medium text-gray-900">{t('manageSubscription', 'Manage Subscription')}</p>
                      <p className="text-sm text-gray-600 mt-1">{t('manageSubscriptionDesc', 'Update payment methods, view invoices, change or cancel your plan.')}</p>
                    </div>
                    <button onClick={handleOpenPortal} disabled={portalLoading}
                      className="px-5 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center gap-2 flex-shrink-0 disabled:opacity-50"
                      data-testid="manage-billing-btn">
                      {portalLoading ? (
                        <><Loader2 className="h-4 w-4 animate-spin" /> {t('processing')}</>
                      ) : (
                        <><CreditCard className="h-4 w-4" /> {t('manageSubscription', 'Manage Subscription')}</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
